import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Card } from '../components/SubscriptionAppCard';
import { Button } from '../components/SubscriptionAppButton';
import { Badge } from '../components/SubscriptionAppBadge';
import { Input } from '../components/SubscriptionAppInput';
import { StatCard } from '../components/StatCard';
import { MembershipJourney } from '../components/contact-detail/MembershipJourney';
import type { MembershipJourneyData } from '../components/contact-detail/MembershipJourney';
import { ContactLoyaltyTab } from '../components/contact-detail/ContactLoyaltyTab';
import { TagInput } from '../components/TagInput';
import type { TagData } from '../components/TagInput';
import { OrdersTable } from '../components/OrdersTable';
import { ActivityTimeline } from '../components/subscription-detail/ActivityTimeline';
import { PaymentMethodModal, PaymentData } from '../components/subscription-detail/PaymentMethodModal';
import { ProductSelector } from '../components/subscription-detail/ProductSelector';
import { CreateTaskModal } from '../components/CreateTaskModal';
import { TopProductsCard } from '../components/contact-detail/TopProductsCard';
import { ContactHeaderDetails } from '../components/contact-detail/ContactHeaderDetails';
import { ContactCallFlag } from '../components/contact-detail/ContactCallFlag';
import { PaymentProblemBanner } from '../components/contact-detail/PaymentProblemBanner';
import { ContactSidePanel } from '../components/contact-detail/ContactSidePanel';
import { ContactSummaryCard } from '../components/contact-detail/ContactSummaryCard';
import { SpendingTrendStrip } from '../components/contact-detail/SpendingTrendStrip';
import { ContactPrototypeStates } from '../components/contact-detail/ContactPrototypeStates';
import type { ContactPrototypeState } from '../components/contact-detail/ContactPrototypeStates';
import { contactQuarterlySpend, contactQuarterlySpendDeclining, recentExtraPurchaseDemoOrder } from '../data/mockContactSpending';
import { LogCallModal } from '../components/contact-detail/LogCallModal';
import type { LoggedCall } from '../components/contact-detail/LogCallModal';
import { DropdownMenu } from '../components/SubscriptionAppDropdownMenu';
import type { DropdownMenuItem } from '../components/SubscriptionAppDropdownMenu';
import { OneTimeOrderModal } from '../components/subscription-detail/OneTimeOrderModal';
import { oneTimeOrderBundles } from '../data/mockOneTimeOrder';
import { subscriptionItemsToOrderLines } from '../utils/oneTimeOrder';
import { contactCallInfo } from '../data/mockContactCallInfo';
import type { CallFlag } from '../data/mockContactCallInfo';
import { formatShortDate } from '../utils/dateFormat';
import { RecentPurchasesCard } from '../components/contact-detail/RecentPurchasesCard';
import { OrderingCadenceCard } from '../components/contact-detail/OrderingCadenceCard';
import { CellarDoorVisitsCard } from '../components/contact-detail/CellarDoorVisitsCard';
import { contactVisits, formatVisitTotal, sortVisitsNewestFirst } from '../data/mockContactVisits';
import { ContactSubscriptionsTab } from '../components/contact-detail/ContactSubscriptionsTab';
import { HoverTooltip } from '../components/contact-detail/HoverTooltip';
import { contactSubscriptions as initialContactSubscriptions } from '../data/mockContactSubscriptions';
import { contactOrders } from '../data/mockContactOrders';
import { OrdersChannelSummary } from '../components/contact-detail/OrdersChannelSummary';
import type { Order } from '../components/OrdersTable/OrdersTable';
import { channelBadgeVariant, matchesChannelFilter, orderTotal, summarizeChannels } from '../utils/orderChannels';
import type { OrderChannelFilter } from '../utils/orderChannels';
import type { ContactSubscription } from '../data/mockContactSubscriptions';
import type { TaskData } from '../components/CreateTaskModal';
import { subscriptions, availableProducts, getItemPrice } from '../data/mockSubscriptionData';
import { useActivities } from '../contexts/ActivityContext';
import { useTasks } from '../contexts/TaskContext';
import { useTags } from '../contexts/TagsContext';
import { useAppMode } from '../contexts/AppModeContext';
import { ArrowLeft, ExternalLink, User as UserIcon, Edit, Save, X, Mail, Phone, MapPin, Copy, Check, DollarSign, TrendingDown, Package, Globe, CreditCard, ArrowRight, ShoppingCart, Calendar, CheckCircle, Circle, Download, ChevronDown, Wine, Wand2, Loader2 } from 'lucide-react';
// Staff member using the app, shown on logged calls
const CURRENT_STAFF_NAME = 'Jude';
// Mock contact data - in real app, fetch from API
const mockContactData = {
  '1': {
    id: '1',
    name: 'Sarah Johnson',
    email: 'sarah.j@example.com',
    phone: '+1 (555) 123-4567',
    address: {
      street: '123 Main St',
      city: 'San Francisco',
      state: 'CA',
      zip: '94102',
      country: 'United States'
    },
    status: 'Active',
    // Loyalty fields (mirrors Loyalty Members) — consumed by ContactLoyaltyTab.
    loyaltyStatus: 'Active' as 'Active' | 'Paused' | 'Expired',
    tierLocked: true,
    tierLockedBy: 'Sarah Chen',
    tierLockedAt: 'Feb 10, 2026',
    walletPassState: 'Active' as 'NotIssued' | 'Sent' | 'Active' | 'SyncIssue' | 'Revoked',
    walletPassLastSyncedAt: 'Feb 8, 2026',
    walletPassSentAt: 'Mar 15, 2025',
    walletPassRevokedAt: null as string | null,
    subscriptionId: 'SUB-23144',
    subscriptionTitle: 'Premium Wine Club - 6 Pack',
    paymentStatus: 'Current',
    atRisk: false,
    tags: ['VIP', 'Red Wine'],
    memberSince: '2023-01-15',
    nextBilling: '2024-01-15',
    ltv: 1250.0,
    totalOrders: 12,
    subscriptionSpend: 1250.0,
    subscriptionOrders: 12,
    streak: 12,
    lastOrder: '2023-12-15',
    aov: 104.16,
    communicationPreferences: {
      emailMarketing: 'subscribed',
      smsMarketing: 'unsubscribed',
      transactional: 'subscribed'
    },
    paymentMethod: {
      type: 'Visa',
      last4: '4242',
      expiry: '01/26'
    },
    shopifyTags: ['Wholesale', 'Repeat Customer'],
    // Long-tenured member: 4 yrs, many renewals + a few notable events.
    journey: {
      note: "Dashed line marks what's still to come. The 4yr bonus is a reason to stay — and a reason for the team to reach out.",
      // Loyalty data isn't live yet — sample milestones (earned + upcoming).
      // The 3yr bonus coincides with the Mar 2025 anniversary event below, so
      // it merges into that single node. The 4yr bonus is still upcoming and
      // renders to the right of "present" with a relative label.
      loyalty: [{
        date: '2025-03-05',
        label: '3yr bonus',
        state: 'earned'
      }, {
        date: '2026-08-20',
        label: '4yr bonus',
        state: 'upcoming'
      }],
      entries: [{
        date: '2022-03-05',
        kind: 'joined',
        label: 'Joined'
      }, {
        date: '2022-06-05',
        kind: 'renewal',
        label: 'Renewal'
      }, {
        date: '2022-09-05',
        kind: 'renewal',
        label: 'Renewal'
      }, {
        date: '2022-12-05',
        kind: 'renewal',
        label: 'Renewal'
      }, {
        date: '2023-01-18',
        kind: 'event',
        label: 'Payment recovered',
        sub: 'Visa retry succeeded'
      }, {
        date: '2023-03-05',
        kind: 'renewal',
        label: 'Renewal'
      }, {
        date: '2023-06-05',
        kind: 'renewal',
        label: 'Renewal'
      }, {
        date: '2023-08-12',
        kind: 'event',
        label: 'Paused',
        sub: 'Skipped 1 box'
      }, {
        date: '2023-12-05',
        kind: 'renewal',
        label: 'Renewal'
      }, {
        date: '2024-03-05',
        kind: 'renewal',
        label: 'Renewal'
      }, {
        date: '2024-06-05',
        kind: 'renewal',
        label: 'Renewal'
      }, {
        date: '2024-09-05',
        kind: 'renewal',
        label: 'Renewal'
      }, {
        date: '2024-11-02',
        kind: 'event',
        label: 'Plan downgraded',
        sub: '12-pack → 6-pack'
      }, {
        date: '2025-03-05',
        kind: 'event',
        label: '3yr anniversary',
        sub: 'Loyalty bonus earned'
      }, {
        date: '2025-06-05',
        kind: 'renewal',
        label: 'Renewal'
      }, {
        date: '2025-09-05',
        kind: 'renewal',
        label: 'Renewal'
      }, {
        date: '2025-12-05',
        kind: 'renewal',
        label: 'Renewal'
      }, {
        date: '2026-03-05',
        kind: 'renewal',
        label: 'Renewal'
      }, {
        date: '2026-09-05',
        kind: 'nextBilling',
        label: 'Next billing',
        sub: 'Sep 5'
      }]
    }
  }
};
interface ContactDetailPageProps {
  currentPage?: string;
  onNavigate?: (page: string) => void;
}
export function ContactDetailPage({
  currentPage = 'Contacts',
  onNavigate
}: ContactDetailPageProps) {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    isLoyaltyOnly
  } = useAppMode();
  const {
    getActivitiesByCustomer,
    addActivity
  } = useActivities();
  const storedActivities = getActivitiesByCustomer(id || '1');
  const {
    addTask
  } = useTasks();
  const {
    allTags,
    getCustomerTags,
    addTagToCustomer,
    removeTagFromCustomer,
    createTag
  } = useTags();
  const contactTags = getCustomerTags(id || '1');
  // Loyalty Members rows route here by member id. Only contact '1' has a full
  // mock record, so for ids 2-8 we resolve a contact from the members roster
  // (correct name/email in the header) reusing contact 1 as the field template.
  const LOYALTY_MEMBER_OVERRIDES: Record<string, {
    name: string;
    email: string;
  }> = {
    '2': {
      name: 'James Chen',
      email: 'james.c@example.com'
    },
    '3': {
      name: 'Emma Wilson',
      email: 'emma.w@example.com'
    },
    '4': {
      name: 'David Park',
      email: 'david.p@example.com'
    },
    '5': {
      name: 'Lisa Wang',
      email: 'lisa.w@example.com'
    },
    '6': {
      name: 'Tom Harris',
      email: 'tom.h@example.com'
    },
    '7': {
      name: 'Sophie Miller',
      email: 'sophie.m@example.com'
    },
    '8': {
      name: 'Michael Brown',
      email: 'michael.b@example.com'
    },
    '9': {
      name: 'Sarah Larson',
      email: 'sarah.larson@example.com'
    }
  };
  const baseContact = mockContactData[id as keyof typeof mockContactData];
  const memberOverride = id ? LOYALTY_MEMBER_OVERRIDES[id] : undefined;
  const contact = baseContact ? baseContact : memberOverride ? {
    ...mockContactData['1'],
    id,
    name: memberOverride.name,
    email: memberOverride.email
  } : undefined;
  const actualSubscription = useMemo(() => {
    return subscriptions.find(sub => sub.id === contact?.subscriptionId);
  }, [contact?.subscriptionId]);
  const [isEditing, setIsEditing] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [isSendingMagicLink, setIsSendingMagicLink] = useState(false);
  const [callFlag, setCallFlag] = useState<CallFlag>(contactCallInfo.callFlag);
  const [lastContacted, setLastContacted] = useState(contactCallInfo.lastContacted);
  const [memberSubscriptions, setMemberSubscriptions] = useState<ContactSubscription[]>(initialContactSubscriptions);
  const [orderChannelFilter, setOrderChannelFilter] = useState<OrderChannelFilter>('all');
  const orderChannelSummary = useMemo(() => summarizeChannels(contactOrders), []);
  // Past orders for the Orders tab, newest first, with their channel badge
  const filteredRecentOrders: Order[] = useMemo(() => [...contactOrders].sort((a, b) => b.date.localeCompare(a.date)).filter(order => matchesChannelFilter(order.channel, orderChannelFilter)).map(order => {
    const subtotal = orderTotal(order);
    const tax = subtotal * 0.1;
    const money = (value: number) => "$" + value.toFixed(2);
    return {
      id: order.orderNumber,
      date: formatShortDate(order.date),
      orderId: order.orderNumber,
      chargeId: `#11802${order.orderNumber.replace('#', '')}`,
      amount: money(subtotal),
      status: 'success' as const,
      chargeCreatedAt: `${formatShortDate(order.date)} at 8:00 AM`,
      paymentProcessor: 'Shopify Payments',
      channel: {
        label: order.channel,
        variant: channelBadgeVariant(order.channel)
      },
      items: order.items.map(item => ({
        name: `${item.vintage} ${item.title}`,
        quantity: item.quantity,
        price: money(item.price)
      })),
      subtotal: money(subtotal),
      taxes: money(tax),
      shipping: '$0.00',
      total: money(subtotal + tax)
    };
  }), [orderChannelFilter]);
  // Demo-only states from the Prototype states bar
  const [prototypeState, setPrototypeState] = useState<ContactPrototypeState>({
    paymentProblem: true,
    overdueExtraPurchase: true,
    multipleSubscriptions: true,
    spendingUp: true,
    hasSkipsPauses: true,
    hasCellarDoorVisits: true
  });
  // Cellar door visits come from POS orders with this customer, for every contact
  const visits = useMemo(() => prototypeState.hasCellarDoorVisits ? sortVisitsNewestFirst(contactVisits) : [], [prototypeState.hasCellarDoorVisits]);
  const visitsLine = visits.length ? `Last visit ${formatShortDate(visits[0].date)} · ${visits[0].location} · ${visits.length} ${visits.length === 1 ? 'visit' : 'visits'}` : 'No cellar door visits yet';
  // Each visit also shows in Activity, merged with the stored activity, newest first
  const contactActivities = useMemo(() => [...storedActivities, ...visits.map((visit, index) => ({
    id: -1 - index,
    type: 'cellar_door_visit',
    date: visit.date,
    title: 'Visited cellar door',
    description: `${visit.location} · ${formatVisitTotal(visit.saleTotal)} sale · order ${visit.orderNumber}`,
    author: visit.staff,
    customerId: id || '1'
  }))].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()), [storedActivities, visits, id]);
  const visibleSubscriptions = useMemo(() => memberSubscriptions.filter(sub => prototypeState.multipleSubscriptions || sub.id !== 'SUB-23138').map(sub => prototypeState.hasSkipsPauses ? sub : {
    ...sub,
    skips12m: 0,
    pauses12m: 0
  }), [memberSubscriptions, prototypeState.multipleSubscriptions, prototypeState.hasSkipsPauses]);
  const cadenceOrders = useMemo(() => prototypeState.overdueExtraPurchase ? contactOrders : [recentExtraPurchaseDemoOrder, ...contactOrders], [prototypeState.overdueExtraPurchase]);
  const currentSubscriptions = visibleSubscriptions.filter(sub => sub.status !== 'Cancelled');
  const totalSkips = currentSubscriptions.reduce((total, sub) => total + sub.skips12m, 0);
  const totalPauses = currentSubscriptions.reduce((total, sub) => total + sub.pauses12m, 0);
  const nextBillingDate = currentSubscriptions.filter(sub => sub.status === 'Active' && sub.nextBilling).map(sub => sub.nextBilling as string).sort()[0];
  const handleSkipNext = (subscriptionId: string) => {
    setMemberSubscriptions(prev => prev.map(sub => {
      if (sub.id !== subscriptionId || !sub.nextBilling) return sub;
      const [y, m, d] = sub.nextBilling.split('-').map(Number);
      const next = new Date(y, m - 1 + sub.intervalMonths, d);
      return {
        ...sub,
        nextBilling: `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}`,
        skips12m: sub.skips12m + 1
      };
    }));
  };
  const handleTogglePause = (subscriptionId: string) => {
    setMemberSubscriptions(prev => prev.map(sub => sub.id !== subscriptionId ? sub : sub.status === 'Paused' ? {
      ...sub,
      status: 'Active'
    } : {
      ...sub,
      status: 'Paused',
      pauses12m: sub.pauses12m + 1
    }));
  };
  const [isLogCallModalOpen, setIsLogCallModalOpen] = useState(false);
  const [isOneTimeOrderOpen, setIsOneTimeOrderOpen] = useState(false);
  const hasActiveSubscription = !isLoyaltyOnly && actualSubscription?.status === 'Active';
  const [searchParams] = useSearchParams();
  // In "Loyalty only" mode there are no subscriptions or allocations, so those
  // tabs are hidden and Loyalty becomes the default/home tab. In "Wine club +
  // loyalty" mode the tab bar is unchanged.
  const TAB_NAMES = isLoyaltyOnly ? ['Overview', 'Orders', 'Loyalty'] : ['Overview', 'Subscriptions', 'Orders', 'Loyalty', 'Allocations'];
  const defaultTab = isLoyaltyOnly ? 'Loyalty' : 'Overview';
  const tabParam = searchParams.get('tab');
  const resolveTab = (param: string | null) => TAB_NAMES.find(t => t.toLowerCase() === (param || '').toLowerCase()) || defaultTab;
  const [activeTab, setActiveTab] = useState(resolveTab(tabParam));
  // Honour ?tab= deep links (e.g. Members -> /contacts/:id?tab=loyalty),
  // including when navigating between contacts without a remount.
  useEffect(() => {
    if (tabParam) setActiveTab(resolveTab(tabParam));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabParam, id]);
  // If the app mode changes while a now-hidden tab is active (e.g. switching to
  // Loyalty-only while on Subscriptions/Allocations), fall back to the mode's
  // default tab so we never render a hidden tab's content.
  useEffect(() => {
    if (!TAB_NAMES.includes(activeTab)) setActiveTab(defaultTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoyaltyOnly]);
  const [formData, setFormData] = useState({
    name: contact?.name || '',
    email: contact?.email || '',
    phone: contact?.phone || '',
    street: contact?.address.street || '',
    city: contact?.address.city || '',
    state: contact?.address.state || '',
    zip: contact?.address.zip || '',
    country: contact?.address.country || '',
    emailMarketing: contact?.communicationPreferences.emailMarketing || 'subscribed',
    smsMarketing: contact?.communicationPreferences.smsMarketing || 'unsubscribed',
    transactional: contact?.communicationPreferences.transactional || 'subscribed'
  });
  const [paymentMethod, setPaymentMethod] = useState(contact?.paymentMethod || {
    type: 'Visa',
    last4: '4242',
    expiry: '01/26'
  });
  const isCardExpiringSoon = (expiry: string) => {
    const [month, year] = expiry.split('/');
    const expiryDate = new Date(2000 + parseInt(year), parseInt(month) - 1);
    const today = new Date();
    const sixtyDaysFromNow = new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000);
    return expiryDate <= sixtyDaysFromNow && expiryDate >= today;
  };
  const isCardExpired = (expiry: string) => {
    const [month, year] = expiry.split('/');
    const expiryDate = new Date(2000 + parseInt(year), parseInt(month) - 1);
    const today = new Date();
    return expiryDate < today;
  };
  const handleNavigate = onNavigate || ((page: string) => {
    if (page === 'Contacts') navigate('/contacts');else navigate('/');
  });
  const handleSendMagicLink = async () => {
    setIsSendingMagicLink(true);
    try {
      console.log('Sending magic link...');
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Show success toast (in real app)
      alert(`Magic link sent to ${formData.email}`);
    } finally {
      setIsSendingMagicLink(false);
    }
  };
  const handleSave = () => {
    console.log('Saving contact:', formData);
    setIsEditing(false);
  };
  const handleCancel = () => {
    setFormData({
      name: contact?.name || '',
      email: contact?.email || '',
      phone: contact?.phone || '',
      street: contact?.address.street || '',
      city: contact?.address.city || '',
      state: contact?.address.state || '',
      zip: contact?.address.zip || '',
      country: contact?.address.country || '',
      emailMarketing: contact?.communicationPreferences.emailMarketing || 'subscribed',
      smsMarketing: contact?.communicationPreferences.smsMarketing || 'unsubscribed',
      transactional: contact?.communicationPreferences.transactional || 'subscribed'
    });
    setIsEditing(false);
  };
  const handlePaymentUpdate = (paymentData: PaymentData) => {
    const firstDigit = paymentData.cardNumber.charAt(0);
    let cardType = 'Card';
    if (firstDigit === '4') cardType = 'Visa';else if (firstDigit === '5') cardType = 'Mastercard';else if (firstDigit === '3') cardType = 'Amex';
    const last4 = paymentData.cardNumber.replace(/\s/g, '').slice(-4);
    const expiry = `${paymentData.expiryMonth}/${paymentData.expiryYear}`;
    setPaymentMethod({
      type: cardType,
      last4,
      expiry
    });
  };
  const handleAddTag = (tag: TagData) => {
    addTagToCustomer(id || '1', tag);
  };
  const handleRemoveTag = (tagId: string) => {
    removeTagFromCustomer(id || '1', tagId);
  };
  const handleCreateTag = (label: string, color?: string) => {
    const newTag: TagData = {
      id: label.toLowerCase().replace(/\s+/g, '-'),
      label,
      color: (color || 'blue') as 'blue' | 'green' | 'orange' | 'purple' | 'gray'
    };
    createTag(newTag);
    addTagToCustomer(id || '1', newTag);
  };
  const handleAddNote = (noteText: string) => {
    addActivity(id || '1', {
      type: 'note',
      date: new Date().toLocaleDateString('en-GB').split('/').join('-'),
      title: 'Admin Note',
      description: noteText,
      author: 'Sarah Admin'
    });
  };
  // Logs the call to Activity and updates "Last contacted"
  const handleLogCall = (call: LoggedCall) => {
    const caller = CURRENT_STAFF_NAME;
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    addActivity(id || '1', {
      type: 'call',
      date: today,
      title: 'Call logged',
      description: [`Call · ${call.outcome} · by ${caller}`, call.note].filter(Boolean).join('\n'),
      author: caller
    });
    setLastContacted({
      date: today,
      method: call.outcome,
      by: caller
    });
    setIsLogCallModalOpen(false);
  };
  const handleAddTask = (task: TaskData) => {
    addTask({
      title: task.title,
      assignee: {
        name: task.assignee,
        avatar: task.assignee.split(' ').map(n => n[0]).join(''),
        email: `${task.assignee.toLowerCase().replace(' ', '.')}@acmewine.com`
      },
      priority: task.priority as any,
      status: 'Open',
      dueDate: task.dueDate,
      linkedTo: {
        id: id || '1',
        type: 'contact',
        label: contact.name
      },
      createdBy: 'Sarah Chen'
    });
  };
  // Card problem to raise on the call: expired card, or a failing payment
  const paymentProblemMessage = !prototypeState.paymentProblem ? null : isCardExpired(paymentMethod.expiry) ? `Card ending ${paymentMethod.last4} expired ${paymentMethod.expiry}. Update it on the call.` : `Payments on card ending ${paymentMethod.last4} are failing. Update it on the call.`;
  const moreActions: DropdownMenuItem[] = [...(hasActiveSubscription ? [{
    label: 'Create one-time order',
    icon: <ShoppingCart className="w-4 h-4 text-[#64748B]" data-id="element-8057" data-mp="24jbrz2k3d3on" />,
    onClick: () => setIsOneTimeOrderOpen(true)
  }] : []), ...(!isLoyaltyOnly && contact ? [{
    label: 'View subscription',
    icon: <Package className="w-4 h-4 text-[#64748B]" data-id="element-8058" data-mp="1csxhzhf78bi9" />,
    onClick: () => navigate(`/subscriptions/${contact.subscriptionId}`)
  }] : []), {
    label: 'View in Shopify',
    icon: <ExternalLink className="w-4 h-4 text-[#64748B]" data-id="element-8059" data-mp="1grmwjdbz3z3h" />,
    onClick: () => window.open(`https://admin.shopify.com/customers/${id}`, '_blank')
  }, {
    label: isSendingMagicLink ? 'Sending magic link…' : 'Send magic link',
    icon: isSendingMagicLink ? <Loader2 className="w-4 h-4 text-[#64748B] animate-spin" data-id="element-8060" data-mp="t3n3g6r6vwlq" /> : <Wand2 className="w-4 h-4 text-[#64748B]" data-id="element-8061" data-mp="y382sia9u6yz" />,
    onClick: () => {
      if (!isSendingMagicLink) handleSendMagicLink();
    }
  }];
  const lastContactedLine = `Last contacted ${formatShortDate(lastContacted.date)} · ${lastContacted.method} · by ${lastContacted.by}`;
  const contactNotes = contactActivities.filter(activity => activity.type === 'note');
  if (!contact) {
    return <div className="flex h-screen bg-[#F1F5F9] items-center justify-center" data-id="element-8062" data-mp="39kb8edbvtwww">
        <div className="text-center" data-id="element-8063" data-mp="34qgrfhnnvw1i">
          <h2 className="text-xl font-semibold text-[#0F172A] mb-2" data-id="element-8064" data-mp="3jgeflxllclqp">
            Contact not found
          </h2>
          <Button onClick={() => navigate('/contacts')} data-id="element-8065" data-mp="3eics90h4u09f">
            Back to Contacts
          </Button>
        </div>
      </div>;
  }
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-8066" data-mp="3sgjjhxrkt204">
      <ContactPrototypeStates state={prototypeState} onChange={setPrototypeState} callFlag={callFlag} onCallFlagChange={setCallFlag} data-id="element-8067" data-mp="3of7spowyuaqc" />

      {/* Header */}
      <div className="mb-6" data-id="element-8068" data-mp="1y5e98alzhrbt">
        <div className="flex items-start justify-between gap-6" data-id="element-8069" data-mp="1u1zqhc6nuwuo">
          <div className="flex items-start gap-4 min-w-0 flex-1" data-id="element-8070" data-mp="1u3t090cklx6w">
            <button onClick={() => navigate('/contacts')} className="flex items-center justify-center w-10 h-10 rounded-md border border-[#CBD5E1] bg-white hover:bg-[#F8FAFC] transition-colors" data-id="element-8071" data-mp="1jyujgx1hhdou">
              <ArrowLeft className="w-5 h-5 text-[#475569]" data-id="element-8072" data-mp="1a6x5jij3c7mf" />
            </button>
            <ContactHeaderDetails fields={formData} isEditing={isEditing} onChange={updates => setFormData({
            ...formData,
            ...updates
          })} nameAdornment={<ContactCallFlag value={callFlag} onChange={setCallFlag} data-id="element-8074" data-mp="158vkis23ty8b" />} clubLabels={isLoyaltyOnly ? undefined : currentSubscriptions.map(sub => `${sub.club} · ${sub.plan}`)} footnote={[lastContactedLine, visitsLine]} data-id="element-8073" data-mp="1f6jjxvanntwt" />
          </div>

          <div className="flex items-center gap-3 flex-shrink-0" data-id="element-8075" data-mp="w7fd8t6l6c8b">
            {!isEditing ? <>
                <Button variant="outline" iconLeft={<Phone className="w-4 h-4" data-id="element-8077" data-mp="2lnwrzxirbs8j" />} onClick={() => setIsLogCallModalOpen(true)} data-id="element-8076" data-mp="2lo6lvw6xajji">
                  Log call
                </Button>
                <Button variant="primary" iconLeft={<Edit className="w-4 h-4" data-id="element-8079" data-mp="2o8coo0jsw3yh" />} onClick={() => setIsEditing(true)} data-id="element-8078" data-mp="2o82x48j3xoqo">
                  Edit
                </Button>
                <DropdownMenu label="More actions" items={moreActions} data-id="element-8080" data-mp="2k82h4jgd15hi" />
              </> : <>
                <Button variant="outline" onClick={handleCancel} data-id="element-8081" data-mp="2f8zet6rrldd8">
                  Cancel
                </Button>
                <Button variant="primary" iconLeft={<Save className="w-4 h-4" data-id="element-8083" data-mp="37vtyw2ko6aq6" />} onClick={handleSave} data-id="element-8082" data-mp="37vkfwyy1l3tz">
                  Save Changes
                </Button>
              </>}
          </div>
        </div>
      </div>

      {paymentProblemMessage && <PaymentProblemBanner message={paymentProblemMessage} onUpdatePaymentMethod={() => setIsPaymentModalOpen(true)} data-id="element-8084" data-mp="2y4piqzj9cjtr" />}

      {/* Tab Bar */}
      <div className="border-b border-[#E2E8F0] mb-6" data-id="element-8085" data-mp="2t4jultc75fgf">
        <div className="flex gap-6" data-id="element-8086" data-mp="3qn3g6y0om3sb">
          {TAB_NAMES.map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-3 text-sm font-medium relative transition-colors ${activeTab === tab ? 'text-[#0EA5E9]' : 'text-[#475569] hover:text-[#0F172A]'}`} data-id="element-8087" data-mp="3lvbqsqt9i43r">
              {tab}
              {activeTab === tab && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0EA5E9] rounded-t-full" data-id="element-8088" data-mp="20oyy2lx9gjr" />}
            </button>)}
        </div>
      </div>

      {/* Tab content with the persistent notes and tags panel on the right */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-6 items-start" data-id="element-8089" data-mp="bw9xtrvp20pa">
      <div className="min-w-0" data-id="element-8090" data-mp="fxvgxuytnb7x">
      {/* Overview Tab Content */}
      {activeTab === 'Overview' && <div className="space-y-6" data-id="element-8091" data-mp="kx7yq1au5s3n">
          {/* Key Metrics Band - Full Width */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-id="element-8092" data-mp="pq9ionpwghu3">
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-5 shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-8093" data-mp="unt01whw9aq9">
              <div className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider mb-2" data-id="element-8094" data-mp="zpibhd913qev">
                Lifetime value
              </div>
              <div className="text-[28px] font-semibold text-[#0F172A] leading-none" data-id="element-8095" data-mp="1p07ir6hnukvy">
                ${contact.ltv.toLocaleString('en-US')}
              </div>
            </div>
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-5 shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-8096" data-mp="1l18bhf8gic1c">
              <div className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider mb-2" data-id="element-8097" data-mp="1tztv7dpnzx3p">
                Renewals
              </div>
              <div className="text-[28px] font-semibold text-[#0F172A] leading-none" data-id="element-8098" data-mp="16aizwbr32uwy">
                {contact.subscriptionOrders}
              </div>
            </div>
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-5 shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-8099" data-mp="1g02jxkqj8ute">
              <div className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider mb-2" data-id="element-8100" data-mp="1b8198390jc5h">
                Member since
              </div>
              <div className="text-[28px] font-semibold text-[#0F172A] leading-none" data-id="element-8101" data-mp="nh5f2zh344v2">
                1y 4m
              </div>
            </div>
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-5 shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-8102" data-mp="2u6pfz1dn44q6">
              <div className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider mb-2" data-id="element-8103" data-mp="2z01tafcvcp3m">
                Next billing
              </div>
              <div className="text-[28px] font-semibold text-[#0F172A] leading-none" data-id="element-8104" data-mp="2p8e6wsv65y90">
                {nextBillingDate ? new Date(`${nextBillingDate}T00:00:00`).toLocaleDateString('en-AU', {
                  day: 'numeric',
                  month: 'short'
                }) : '—'}
              </div>
            </div>
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-5 shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-8105" data-mp="33y3eb5tuuyhq">
              <div className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider mb-2" data-id="element-8106" data-mp="38vlot0nitlru">
                Skips &amp; pauses (last 12 months)
              </div>
              <HoverTooltip align="right" content={currentSubscriptions.length ? <ul className="space-y-1" data-id="element-8108" data-mp="1xfev2ipzaq4v">
                    {currentSubscriptions.map(sub => <li key={sub.id} className="flex justify-between gap-4" data-id="element-8109" data-mp="vjpz8iyrnecg">
                        <span data-id="element-8110" data-mp="10i9r1001jkcq">{sub.club}</span>
                        <span className="text-[#CBD5E1] whitespace-nowrap" data-id="element-8111" data-mp="10l5pcpfswb1p">
                          {sub.skips12m} {sub.skips12m === 1 ? 'skip' : 'skips'} · {sub.pauses12m} {sub.pauses12m === 1 ? 'pause' : 'pauses'}
                        </span>
                      </li>)}
                  </ul> : 'No current subscriptions'} data-id="element-8107" data-mp="1xh805tky12ar">
                <span className="text-xl font-semibold text-[#0F172A] leading-none cursor-default border-b border-dashed border-[#CBD5E1]" data-id="element-8112" data-mp="hsbhefglhj6z">
                  {totalSkips} {totalSkips === 1 ? 'skip' : 'skips'} &middot;{' '}
                  {totalPauses} {totalPauses === 1 ? 'pause' : 'pauses'}
                </span>
              </HoverTooltip>
            </div>
          </div>

          {/* Membership Journey - slim strip, collapsed by default */}
          <MembershipJourney data={contact.journey as MembershipJourneyData} collapsible data-id="element-8113" data-mp="3u8f5ucia3v5j" />

          {/* Spending trend - slim strip, collapsed by default */}
          <SpendingTrendStrip data={prototypeState.spendingUp ? contactQuarterlySpend : contactQuarterlySpendDeclining} data-id="element-8114" data-mp="3hre45o57u8br" />

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-8115" data-mp="3drvjmthb3z9g">
            {/* Left Column - Main Content (2/3) */}
            <div className="lg:col-span-2 space-y-6" data-id="element-8116" data-mp="33rl6t9z6otut">
              <ContactSummaryCard data-id="element-8117" data-mp="33t5ymru45yoz" />

              {/* Activity Timeline - all activity; notes also live in the side panel */}
              <Card padding="none" data-id="element-8118" data-mp="2u21k1t8cofuo">
                <ActivityTimeline events={contactActivities} onAddNote={handleAddNote} onAddTask={() => setIsCreateTaskModalOpen(true)} data-id="element-8119" data-mp="2u19xf7wqdsgj" />
              </Card>

              <TopProductsCard data-id="element-8120" data-mp="2pyn6d97vwbke" />

              <RecentPurchasesCard onViewAllOrders={() => setActiveTab('Orders')} data-id="element-8121" data-mp="2l1myqtvdz0r6" />
            </div>

            {/* Right Column - Sidebar (1/3) */}
            <div className="space-y-6" data-id="element-8122" data-mp="2g4d9be7zyp3c">
              <OrderingCadenceCard orders={cadenceOrders} subscriptions={visibleSubscriptions} data-id="element-8123" data-mp="26e4yc7ouwsak" />

              <CellarDoorVisitsCard visits={visits} data-id="element-8124" data-mp="2bean9g3qs0oy" />

              {/* Payment Method Card */}
              <Card padding="none" data-id="element-8125" data-mp="1wh0be5n6sssp">
                <div className="p-6 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-8126" data-mp="1wi1o0ejupncj">
                  <div className="flex items-center justify-between" data-id="element-8127" data-mp="21ftrm5tuyxwk">
                    <h3 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-8128" data-mp="2p67952ethecw">
                      Payment Method
                    </h3>
                    <button onClick={() => setIsPaymentModalOpen(true)} className="text-[13px] text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors" data-id="element-8129" data-mp="2u6br6wvdih58">
                      Update
                    </button>
                  </div>
                </div>

                <div className="p-6 bg-white" data-id="element-8130" data-mp="3838guuzr9skj">
                  <div className="flex items-center gap-3 bg-[#F8FAFC] p-3 rounded-md border border-[#E2E8F0]" data-id="element-8131" data-mp="38277qw2avg5g">
                    <CreditCard className="w-5 h-5 text-[#475569]" data-id="element-8132" data-mp="11fhvyyzpdpa3" />
                    <div className="flex-1" data-id="element-8133" data-mp="11doo5gdcsz0i">
                      <div className="text-[14px] font-medium text-[#0F172A]" data-id="element-8134" data-mp="whge2he0jw07">
                        {paymentMethod.type} ending in {paymentMethod.last4}
                      </div>
                      <div className="flex items-center gap-2 mt-1" data-id="element-8135" data-mp="166q7c0klfkpw">
                        <span className="text-[12px] text-[#94A3B8]" data-id="element-8136" data-mp="168kqb9cb12rc">
                          Expires {paymentMethod.expiry}
                        </span>
                        {isCardExpired(paymentMethod.expiry) && <span className="inline-flex items-center px-2 py-0.5 rounded text-[12px] font-medium bg-[#FEE2E2] text-[#DC2626] border border-[#FECACA]" data-id="element-8137" data-mp="djx3vaftk7c7">
                            Expired
                          </span>}
                        {!isCardExpired(paymentMethod.expiry) && isCardExpiringSoon(paymentMethod.expiry) && <span className="inline-flex items-center px-2 py-0.5 rounded text-[12px] font-medium bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A]" data-id="element-8138" data-mp="1yv5i2awozgvf">
                              Expiring Soon
                            </span>}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Communication Preferences Card */}
              <Card padding="none" data-id="element-8139" data-mp="385mjmxxe5p8z">
                <div className="p-6 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-8140" data-mp="385weaytdskl0">
                  <h3 className="text-xl font-semibold text-[#0F172A]" data-id="element-8141" data-mp="3d3od8csg16yp">
                    Communication Preferences
                  </h3>
                </div>

                <div className="p-6 bg-white space-y-4" data-id="element-8142" data-mp="3lvzup95kxk2j">
                  {([{
                    key: 'emailMarketing',
                    label: 'Email Marketing'
                  }, {
                    key: 'smsMarketing',
                    label: 'SMS Marketing'
                  }, {
                    key: 'transactional',
                    label: 'Transactional'
                  }] as const).map(pref => {
                    // On = subscribed; off covers unsubscribed and never subscribed
                    const isOn = formData[pref.key] === 'subscribed';
                    const labelId = `comm-pref-${pref.key}`;
                    return <div key={pref.key} className="flex items-center justify-between" data-id="element-8143" data-mp="1f90u3rtobs32">
                        <span id={labelId} className="text-sm font-medium text-[#475569]" data-id="element-8144" data-mp="1aa7lodt8ur9r">
                          {pref.label}
                        </span>
                        <button type="button" role="switch" aria-checked={isOn} aria-labelledby={labelId} onClick={() => setFormData({
                        ...formData,
                        [pref.key]: isOn ? 'unsubscribed' : 'subscribed'
                      })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${isOn ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-8145" data-mp="22y3hzhewih60">
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isOn ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-8146" data-mp="2luuwzeneua49" />
                        </button>
                      </div>;
                  })}
                </div>
              </Card>
            </div>
          </div>
        </div>}

      {/* Loyalty Tab Content */}
      {activeTab === 'Loyalty' && <ContactLoyaltyTab contactId={contact.id} contactName={contact.name} tierLocked={(contact as any).tierLocked} tierLockedBy={(contact as any).tierLockedBy} tierLockedAt={(contact as any).tierLockedAt} walletPassState={(contact as any).walletPassState} walletPassLastSyncedAt={(contact as any).walletPassLastSyncedAt} walletPassSentAt={(contact as any).walletPassSentAt} walletPassRevokedAt={(contact as any).walletPassRevokedAt} visits={visits} onViewVisits={() => setActiveTab('Overview')} data-id="element-8147" data-mp="f6cfc4i0y31y" />}

      {/* Allocations Tab Content */}
      {activeTab === 'Allocations' && <div className="space-y-8" data-id="element-8148" data-mp="1qm9eeljku3cw">
          {/* Active Allocations Section */}
          <div data-id="element-8149" data-mp="3b6odjug8wiv8">
            <div className="mb-4" data-id="element-8150" data-mp="3b6ejnvs2xrk9">
              <h3 className="text-lg font-semibold text-[#0F172A]" data-id="element-8151" data-mp="3b3a82dcabtjk">
                Active Allocations
              </h3>
              <p className="text-sm text-[#64748B]" data-id="element-8152" data-mp="3kv6cs78vmhwr">
                Allocations currently available to this member
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-8153" data-mp="3g3es2dcej6ej">
              {/* Card 1 */}
              <div onClick={() => navigate('/allocations/1')} className="bg-white border border-[#E2E8F0] rounded-lg p-5 shadow-sm hover:shadow-md hover:border-[#0EA5E9] transition-all cursor-pointer" data-id="element-8154" data-mp="3tu9tj4kkml83">
                <div className="flex justify-between items-start mb-4" data-id="element-8155" data-mp="7o4tvgbmulc1">
                  <div data-id="element-8156" data-mp="2quub7uj4tah">
                    <span className="text-base font-bold text-[#0F172A] block mb-1" data-id="element-8157" data-mp="2ql3ji1nui3q">
                      2023 Reserve Shiraz Release
                    </span>
                    <div className="text-sm text-[#64748B]" data-id="element-8158" data-mp="24hy5pzc8ubw3">
                      1 Mar – 15 Mar 2026
                    </div>
                  </div>
                  <Badge variant="success" data-id="element-8159" data-mp="1im6e5qapu6ey">Active</Badge>
                </div>

                <div className="flex items-center gap-4 mb-4" data-id="element-8160" data-mp="1nl992hwzew97">
                  <div className="flex -space-x-2" data-id="element-8161" data-mp="1sdk8zkrq2i1r">
                    <div className="w-8 h-8 rounded-full bg-[#F1F5F9] border-2 border-white flex items-center justify-center text-[#94A3B8]" data-id="element-8162" data-mp="1sg6ezfyysphv">
                      <Wine className="w-4 h-4" data-id="element-8163" data-mp="zpy7ci221nhj" />
                    </div>
                  </div>
                  <span className="text-sm text-[#475569]" data-id="element-8164" data-mp="14ng65fmcymbt">1 wine</span>
                </div>

                <div className="border-t border-[#F1F5F9] pt-4" data-id="element-8165" data-mp="19mt00kbnctnw">
                  <div className="flex justify-between text-xs mb-1.5" data-id="element-8166" data-mp="19pozid357qeg">
                    <span className="font-medium text-[#0F172A]" data-id="element-8167" data-mp="1dqh7z446xr9o">
                      4 of 6 remaining
                    </span>
                    <span className="text-[#64748B]" data-id="element-8168" data-mp="2p42zgvxiw4yd">33% claimed</span>
                  </div>
                  <div className="h-1.5 w-full bg-[#E2E8F0] rounded-full overflow-hidden" data-id="element-8169" data-mp="2p5xmcfscu753">
                    <div className="h-full bg-[#10B981] rounded-full" style={{
                      width: '33%'
                    }} data-id="element-8170" data-mp="dikaz8ngqr3w" />
                  </div>
                </div>
              </div>

              {/* Card 2 */}
              <div onClick={() => navigate('/allocations/5')} className="bg-white border border-[#E2E8F0] rounded-lg p-5 shadow-sm hover:shadow-md hover:border-[#0EA5E9] transition-all cursor-pointer" data-id="element-8171" data-mp="3vuyvq6lzp9nq">
                <div className="flex justify-between items-start mb-4" data-id="element-8172" data-mp="3lwszo5mv6xqs">
                  <div data-id="element-8173" data-mp="3lyvxhbu9dv62">
                    <span className="text-base font-bold text-[#0F172A] block mb-1" data-id="element-8174" data-mp="3huo95frpqh8d">
                      Vintner Exclusive — Museum Release
                    </span>
                    <div className="text-sm text-[#64748B]" data-id="element-8175" data-mp="3cxxo0e11wvg3">
                      5 Mar – 30 Mar 2026
                    </div>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#F3E8FF] text-[#7E22CE]" data-id="element-8176" data-mp="1b5jars9gcf60">
                    Early Access
                  </span>
                </div>

                <div className="flex items-center gap-4 mb-4" data-id="element-8177" data-mp="k73mmh6bvbwg">
                  <div className="flex -space-x-2" data-id="element-8178" data-mp="f925i1sgpmnm">
                    <div className="w-8 h-8 rounded-full bg-[#F1F5F9] border-2 border-white flex items-center justify-center text-[#94A3B8]" data-id="element-8179" data-mp="f9vc92rf8c5r">
                      <Wine className="w-4 h-4" data-id="element-8180" data-mp="17ysl6f9qswlu" />
                    </div>
                    <div className="w-8 h-8 rounded-full bg-[#F1F5F9] border-2 border-white flex items-center justify-center text-[#94A3B8]" data-id="element-8181" data-mp="1803romukoign">
                      <Wine className="w-4 h-4" data-id="element-8182" data-mp="y7ef1onacyjq" />
                    </div>
                  </div>
                  <span className="text-sm text-[#475569]" data-id="element-8183" data-mp="u5t78hplzage">2 wines</span>
                </div>

                <div className="border-t border-[#F1F5F9] pt-4" data-id="element-8184" data-mp="1qp0mcrtvtcze">
                  <div className="flex justify-between text-xs mb-1.5" data-id="element-8185" data-mp="1ly22xjdljyrx">
                    <span className="font-medium text-[#0F172A]" data-id="element-8186" data-mp="1lwqu355ao0ty">
                      2 of 2 remaining
                    </span>
                    <span className="text-[#64748B]" data-id="element-8187" data-mp="39jlhusrym07g">0% claimed</span>
                  </div>
                  <div className="h-1.5 w-full bg-[#E2E8F0] rounded-full overflow-hidden" data-id="element-8188" data-mp="3dlgj5x9w4dl8">
                    <div className="h-full bg-[#CBD5E1] rounded-full" style={{
                      width: '0%'
                    }} data-id="element-8189" data-mp="3dnt6kpyf3g5o" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Allocation History Section */}
          <div data-id="element-8190" data-mp="3sgwhiehi6vnf">
            <div className="mb-4" data-id="element-8191" data-mp="3sgmsat4a8sis">
              <h3 className="text-lg font-semibold text-[#0F172A]" data-id="element-8192" data-mp="13jqerl2fthu">
                Allocation History
              </h3>
              <p className="text-sm text-[#64748B]" data-id="element-8193" data-mp="54lsdg6dajni">
                Past allocations this member was eligible for
              </p>
            </div>

            <Card padding="none" data-id="element-8194" data-mp="f3ufpq6q900w">
              <div className="overflow-x-auto" data-id="element-8195" data-mp="f31knmh4ryyl">
                <table className="w-full" data-id="element-8196" data-mp="f1qghljrwp6y">
                  <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]" data-id="element-8197" data-mp="2b67mur5ox4zy">
                    <tr data-id="element-8198" data-mp="26863e54cr3ny">
                      <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8199" data-mp="268nzn1r0lhvv">
                        Allocation
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8200" data-mp="2g5k8j8kts60y">
                        Date
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8201" data-mp="2pvvo5sdvg3no">
                        Products
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8202" data-mp="33tk7uww7d4r5">
                        Purchased
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8203" data-mp="19i5f077wap76">
                        Spent
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8204" data-mp="3g6xicsramh9d">
                        Outcome
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]" data-id="element-8205" data-mp="36ft2zrxpgue0">
                    {[{
                      id: '4',
                      name: 'Summer Rosé Pack',
                      date: 'Dec 1 – Dec 20, 2025',
                      products: 3,
                      purchased: 3,
                      spent: '$180.00',
                      outcome: 'Purchased',
                      variant: 'success'
                    }, {
                      id: '3',
                      name: '2022 Single Vineyard Pinot',
                      date: 'Jan 10 – Jan 25, 2026',
                      products: 1,
                      purchased: 6,
                      spent: '$720.00',
                      outcome: 'Purchased',
                      variant: 'success'
                    }, {
                      id: '7',
                      name: 'Autumn Library Collection',
                      date: 'Apr 1 – Apr 15, 2025',
                      products: 4,
                      purchased: 2,
                      spent: '$240.00',
                      outcome: 'Partially Claimed',
                      variant: 'warning'
                    }, {
                      id: '8',
                      name: '2024 Chardonnay Pre-Release',
                      date: 'Nov 5 – Nov 20, 2025',
                      products: 1,
                      purchased: 0,
                      spent: '$0.00',
                      outcome: 'Declined',
                      variant: 'default'
                    }, {
                      id: '9',
                      name: 'Holiday Magnum Special',
                      date: 'Dec 5 – Dec 15, 2025',
                      products: 2,
                      purchased: 0,
                      spent: '$0.00',
                      outcome: 'Expired',
                      variant: 'error'
                    }].map((row, i) => <tr key={i} className="hover:bg-[#F8FAFC]" data-id="element-8206" data-mp="1g4elhqem0noy" data-mpi={i}>
                        <td className="px-6 py-4" data-id="element-8207" data-mp="15yob2hthkgj9">
                          <button onClick={() => navigate(`/allocations/${row.id}`)} className="text-sm font-medium text-[#0EA5E9] hover:underline text-left" data-id="element-8208" data-mp="1aysqs5mkl78f">
                            {row.name}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#64748B]" data-id="element-8209" data-mp="1kwgn2zztqmsp">
                          {row.date}
                        </td>
                        <td className="px-6 py-4 text-sm text-[#0F172A] text-center" data-id="element-8210" data-mp="s8b169x1arra">
                          {row.products}
                        </td>
                        <td className="px-6 py-4 text-sm text-[#0F172A] text-center" data-id="element-8211" data-mp="x5tcg6yixj2g">
                          {row.purchased}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-[#0F172A] text-right" data-id="element-8212" data-mp="4iyxfpkh7bwd">
                          {row.spent}
                        </td>
                        <td className="px-6 py-4" data-id="element-8213" data-mp="9fp9m1t5tnci">
                          <Badge variant={row.variant as any} data-id="element-8214" data-mp="1gc73pz1993a">
                            {row.outcome}
                          </Badge>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </div>}

      {/* Subscriptions Tab Content */}
      {activeTab === 'Subscriptions' && <div className="space-y-6" data-id="element-8215" data-mp="3endwmzqs83wz">
          <ContactSubscriptionsTab subscriptions={visibleSubscriptions} onView={subscriptionId => navigate(`/subscriptions/${subscriptionId}`)} onSkipNext={handleSkipNext} onTogglePause={handleTogglePause} data-id="element-8216" data-mp="3eofcdhiqtel1" />
        </div>}

      {/* Orders Tab Content */}
      {activeTab === 'Orders' && <div className="space-y-6" data-id="element-8217" data-mp="3jmh2gmel6e60">
          <OrdersChannelSummary summary={orderChannelSummary} filter={orderChannelFilter} onFilterChange={setOrderChannelFilter} data-id="element-8218" data-mp="3jlfi1rbokfbm" />
          <OrdersTable key={orderChannelFilter} defaultRecentExpanded recentEmptyMessage="No orders from this channel." recentOrders={filteredRecentOrders} upcomingOrders={matchesChannelFilter('Club', orderChannelFilter) ? [{
            id: 'upcoming-1',
            date: contact.nextBilling,
            orderId: '#4012',
            chargeId: '#1210458823',
            amount: '$126.50',
            status: 'pending',
            chargeCreatedAt: 'Jan 10, 2024 at 8:00 AM',
            paymentProcessor: 'Shopify Payments',
            items: [{
              name: '2025 Fiano',
              quantity: 2,
              price: '$35.00'
            }, {
              name: '2024 Shiraz',
              quantity: 2,
              price: '$45.00'
            }],
            subtotal: '$160.00',
            taxes: '$16.00',
            shipping: '$0.00',
            total: '$176.00'
          }, {
            id: 'upcoming-2',
            date: '2024-02-15',
            orderId: '#4013',
            chargeId: '#1210458824',
            amount: '$142.00',
            status: 'pending',
            chargeCreatedAt: 'Feb 10, 2024 at 8:00 AM',
            paymentProcessor: 'Shopify Payments',
            items: [{
              name: '2024 Cabernet Sauvignon',
              quantity: 3,
              price: '$48.00'
            }, {
              name: '2025 Rosé',
              quantity: 3,
              price: '$32.00'
            }],
            subtotal: '$240.00',
            taxes: '$24.00',
            shipping: '$0.00',
            total: '$264.00'
          }, {
            id: 'upcoming-3',
            date: '2024-03-15',
            orderId: '#4014',
            chargeId: '#1210458825',
            amount: '$98.50',
            status: 'pending',
            chargeCreatedAt: 'Mar 10, 2024 at 8:00 AM',
            paymentProcessor: 'Shopify Payments',
            items: [{
              name: '2025 Sauvignon Blanc',
              quantity: 2,
              price: '$38.00'
            }, {
              name: '2024 Merlot',
              quantity: 2,
              price: '$42.00'
            }],
            subtotal: '$160.00',
            taxes: '$16.00',
            shipping: '$0.00',
            total: '$176.00'
          }, {
            id: 'upcoming-4',
            date: '2024-04-15',
            orderId: '#4015',
            chargeId: '#1210458826',
            amount: '$156.00',
            status: 'pending',
            chargeCreatedAt: 'Apr 10, 2024 at 8:00 AM',
            paymentProcessor: 'Shopify Payments',
            items: [{
              name: '2024 Pinot Noir',
              quantity: 4,
              price: '$52.00'
            }, {
              name: '2025 Chardonnay',
              quantity: 2,
              price: '$40.00'
            }],
            subtotal: '$288.00',
            taxes: '$28.80',
            shipping: '$0.00',
            total: '$316.80'
          }] : []} data-id="element-8219" data-mp="ywhzkrceeva9" />
        </div>}
      </div>

      <ContactSidePanel notes={contactNotes} onAddNote={handleAddNote} appTags={contactTags} availableTags={allTags} onAddTag={handleAddTag} onRemoveTag={handleRemoveTag} onCreateTag={handleCreateTag} shopifyTags={contact.shopifyTags} data-id="element-8220" data-mp="375pkj3vz629h" />
      </div>

      {/* Payment Method Modal */}
      <PaymentMethodModal isOpen={isPaymentModalOpen} onClose={() => setIsPaymentModalOpen(false)} onSave={(data: PaymentData) => {
      handlePaymentUpdate(data);
      setPrototypeState(prev => ({
        ...prev,
        paymentProblem: false
      }));
    }} currentCard={paymentMethod} data-id="element-8221" data-mp="3l4xjbvo878d5" />

      <LogCallModal isOpen={isLogCallModalOpen} contactName={contact.name} onClose={() => setIsLogCallModalOpen(false)} onSave={handleLogCall} data-id="element-8222" data-mp="1vq0vm6z5ut61" />

      {hasActiveSubscription && actualSubscription && <OneTimeOrderModal isOpen={isOneTimeOrderOpen} onClose={() => setIsOneTimeOrderOpen(false)} customerName={contact.name} initialLines={subscriptionItemsToOrderLines(actualSubscription.items || [], availableProducts)} availableProducts={[...availableProducts, ...oneTimeOrderBundles]} shippingAddressLines={[formData.name, formData.street, `${formData.city}, ${formData.state} ${formData.zip}`, formData.country].filter(Boolean)} deliveryMethod="Subscription delivery method" paymentMethodLabel={`${paymentMethod.type} ending ${paymentMethod.last4}`} data-id="element-8223" data-mp="1m2jbbycv5j6i" />}

      {/* Create Task Modal */}
      <CreateTaskModal isOpen={isCreateTaskModalOpen} onClose={() => setIsCreateTaskModalOpen(false)} onCreate={handleAddTask} initialLinkedTo={{
      id: contact.id,
      type: 'contact',
      label: contact.name
    }} data-id="element-8224" data-mp="1v1d57gyt7vgd" />
    </div>;
}